import React, { useCallback, useEffect, useMemo } from "react";

import { Modal, Stack } from "@mui/material";
import { DealSelect } from "components/modals/Select-deal-folder-modal/components/DealSelect";
import { uniq } from "lodash";
import { IAiFile, IFile, IFileUploadType } from "store/types";

import { FileUploader } from "./components/FileUploader";
import { ModalFooter } from "./components/ModalFooter";
import { ModalHeader } from "./components/ModalHeader";
import { WorkspaceFileSelector } from "./components/WorkspaceFileSelector";
import { FilesPickerContext } from "./hooks/files-picker-context";
import { useFilesPickerContextHook } from "./hooks/use-files-picker-context-hook";

export interface FilesPickerModalProps {
  currentFiles: IAiFile[];
  filesPickerModalOpen: boolean;
  onClose: () => void;
  mode?: FilesPickerMode;
  filesPickerSubmit: (data: { filesIds?: number[]; attachments: File[]; files?: IFile[] }) => Promise<void>;
  title: string;
  workspaceTitle: string;
  singleFileSelection?: boolean;
  dealId: number | null;
  getFilesOutsideDeal?: boolean; //Need to use this prop if you need to get files outside of deal without deal selection
  fileUploadType?: IFileUploadType[];
  attachedFiles?: File[];
  selectWithoutFiles?: boolean;
  acceptedFilesExtensions?: string[]; // Array of file extensions (e.g., [".pdf", ".jpg", ".png"])
  onSelectFileForMe?: () => void;
  isFileForMeSelected?: boolean;
}

export enum FilesPickerMode {
  Document = "document"
}

enum FilesPickerStep {
  SelectDeal = "select-deal",
  SelectFiles = "select-files"
}

export const FilesPickerModal: React.FC<FilesPickerModalProps> = ({
  currentFiles,
  filesPickerModalOpen,
  onClose,
  mode,
  filesPickerSubmit,
  title,
  workspaceTitle,
  singleFileSelection = false,
  dealId,
  getFilesOutsideDeal = false,
  fileUploadType,
  attachedFiles,
  selectWithoutFiles = false,
  acceptedFilesExtensions,
  onSelectFileForMe,
  isFileForMeSelected
}) => {
  const filesPickerContext = useFilesPickerContextHook({
    currentFiles,
    isModalOpen: filesPickerModalOpen,
    singleFileSelection,
    dealId,
    getFilesOutsideDeal,
    fileUploadType,
    attachedFiles,
    acceptedFilesExtensions
  });

  const {
    step,
    deal,
    setDeal,
    state,
    attachments,
    oldFiles,
    deletedFilesIds,
    resetState,
    invalidateDataStates,
    setStep,
    setLoading,
    getDealFolders,
    files
  } = filesPickerContext;

  const onSubmit = useCallback(async () => {
    try {
      setLoading(true);
      if (getFilesOutsideDeal) {
        const selectedFileIds = new Set([...state.files.map((f) => f.id), ...oldFiles.map((f) => f.id)]);

        const selectedFiles = files.filter(
          (file) => selectedFileIds.has(file.id) && !deletedFilesIds.includes(file.id)
        );
        await filesPickerSubmit({ files: selectedFiles, attachments });
      } else {
        const selectedFileIds = new Set([...state.files.map((f) => f.id), ...oldFiles.map((f) => f.id)]);

        const selectedFiles = files.filter(
          (file) => selectedFileIds.has(file.id) && !deletedFilesIds.includes(file.id)
        );
        const fileIdsFromStore = state.files.map((item) => item.id);
        const oldFilesIds = oldFiles.map((item) => item.id);
        const filesIds = uniq([...fileIdsFromStore, ...oldFilesIds]).filter((id) => !deletedFilesIds.includes(id));
        await filesPickerSubmit({ filesIds, attachments, files: selectedFiles });
      }

      invalidateDataStates();
      onClose();
    } finally {
      setLoading(false);
    }
  }, [state, oldFiles, attachments, deletedFilesIds, filesPickerSubmit, invalidateDataStates, getFilesOutsideDeal]);

  const handleClose = useCallback(() => {
    onClose();
    resetState();
  }, [onClose, resetState]);

  const handleSelectFileForMe = useCallback(() => {
    resetState();
    onClose();
    onSelectFileForMe?.();
  }, [resetState, onClose, onSelectFileForMe]);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!filesPickerModalOpen) return;

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" || event.key === "Esc") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [filesPickerModalOpen, handleClose]);

  const isSelectDisabled = useMemo(() => {
    if (step === "select-deal") {
      if (!deal && (attachments.length || oldFiles.length)) return false;
      if (!deal && (!attachments.length || !oldFiles.length)) return true;
    }
    if (step === "select-files" && selectWithoutFiles) return false;
    if (step === "select-files" && !state.files.length && !attachments.length && !oldFiles.length) return true;
    return false;
  }, [deal, step, state.files, oldFiles, attachments]);

  const handleSelect = useCallback(() => {
    if (step === "select-deal") {
      if (deal?.id) {
        getDealFolders({ dealId: deal.id });
        setStep("select-files");
        return;
      }
      onSubmit();
      return;
    }
    if (step === "select-files") {
      onSubmit();
    }
  }, [deal, step, onSubmit]);

  const contextValue = useMemo(
    () => ({
      ...filesPickerContext,
      ...(onSelectFileForMe && { onSelectFileForMe: handleSelectFileForMe }),
      isFileForMeSelected
    }),
    [filesPickerContext, onSelectFileForMe, handleSelectFileForMe, isFileForMeSelected]
  );

  return (
    <Modal open={filesPickerModalOpen} onClose={handleClose}>
      <FilesPickerContext.Provider value={contextValue}>
        <Stack
          sx={{
            padding: "32px",
            borderRadius: "8px",
            backgroundColor: "#fff",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "650px"
          }}
          gap="20px"
        >
          <ModalHeader onClose={handleClose} title={title} />
          {step === FilesPickerStep.SelectDeal && mode === FilesPickerMode.Document && (
            <DealSelect deal={deal} setDeal={setDeal} newDealOption={false} />
          )}
          {step === FilesPickerStep.SelectFiles && <WorkspaceFileSelector workspaceTitle={workspaceTitle} />}
          <FileUploader />
          <ModalFooter onClose={handleClose} onSelect={handleSelect} isSelectDisabled={isSelectDisabled} mode={mode} />
        </Stack>
      </FilesPickerContext.Provider>
    </Modal>
  );
};
